import { useLocalRuntime, AssistantRuntimeProvider } from '@assistant-ui/react';
import { Thread, FilesContext, MessageFilesContext, SharedThreadContext } from './assistant-ui/thread';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ProfileModal } from './ProfileModal';
import { SearchChatsModal } from './SearchChatsModal';
import { ConversationStarters } from './ConversationStarters';
import { ShareModal } from './ShareModal';

// Separate component that contains the runtime hook - only rendered after loading
function ChatRuntimeWrapper({ children, threadIdRef, selectedGptId, selectedFiles, setSelectedFiles, setMessageFiles, loadThreads, API_URL, token }) {
  // Use refs to always get the CURRENT value of props inside the async generator
  // This is critical because the generator closure captures stale values otherwise
  const selectedFilesRef = useRef(selectedFiles);
  const setSelectedFilesRef = useRef(setSelectedFiles);
  const selectedGptIdRef = useRef(selectedGptId);

  // Keep refs in sync with props
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    setSelectedFilesRef.current = setSelectedFiles;
  }, [setSelectedFiles]);

  useEffect(() => {
    selectedGptIdRef.current = selectedGptId;
  }, [selectedGptId]);

  const runtime = useLocalRuntime({
    initialMessages: [], // Always start fresh - history is rendered separately
    async *run({ messages, abortSignal }) {
      try {
        const lastMessage = messages[messages.length - 1];
        const userContent = lastMessage.content.find(c => c.type === 'text')?.text || '';

        // Get the CURRENT files from the ref (not the stale closure value)
        const filesToSend = [...selectedFilesRef.current];

        // Clear files immediately so the UI updates right away
        setSelectedFilesRef.current([]);

        const currentFiles = filesToSend.map(file => ({
          id: URL.createObjectURL(file),
          filename: file.name,
          content_type: file.type,
          size: file.size
        }));

        setMessageFiles(prev => [...prev, currentFiles]);

        const formData = new FormData();
        const payload = {
          type: 'items.create',
          params: {
            thread_id: threadIdRef.current,
            message: userContent,
            custom_gpt_id: selectedGptIdRef.current || undefined,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        };

        formData.append('payload', JSON.stringify(payload));
        filesToSend.forEach((file, index) => {
          formData.append(`file_${index}`, file);
        });

        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers,
          body: formData,
          signal: abortSignal
        });

        if (!response.ok) {
          if (response.status === 401) {
            // Token expired/invalid - clear and redirect to login
            localStorage.removeItem('cme_token');
            localStorage.removeItem('cme_user');
            window.location.href = '/';
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';
        let currentStatus = null;
        // Track all non-text content parts (images, charts, file downloads) to preserve them
        let accumulatedParts = [];

        // Helper to build content array with all accumulated parts
        const buildContentParts = (includeStatus = null) => {
          const parts = [];
          if (accumulatedText) {
            parts.push({ type: 'text', text: accumulatedText });
          }
          // Add all accumulated non-text parts (images, charts, etc.)
          parts.push(...accumulatedParts);
          if (includeStatus) {
            parts.push(includeStatus);
          }
          return parts;
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ')) continue;

            try {
              const jsonStr = line.slice(6);
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'status_event') {
                // Status events - show immediately (not typewritten)
                if (parsed.content && parsed.content[0]) {
                  const statusType = parsed.content[0].type || 'log';
                  const statusText = parsed.content[0].text;
                  currentStatus = { type: statusType, text: statusText };
                  yield { content: buildContentParts({ type: 'status', statusType, text: statusText }) };
                }
              } else if (parsed.type === 'thread_stream') {
                // Check for different content types in thread_stream
                const textContent = parsed.content.find(c => c.type === 'text');
                const imageContent = parsed.content.find(c => c.type === 'image');

                if (imageContent && imageContent.data) {
                  // Image content - add to accumulated parts and show immediately
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'image',
                    data: imageContent.data,
                    mime_type: imageContent.mime_type || 'image/png'
                  });
                  yield { content: buildContentParts() };
                } else if (textContent && textContent.text) {
                  // Text content - this gets typewritten
                  accumulatedText += textContent.text;
                  currentStatus = null;
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'chart') {
                // Chart - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.chart_html) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'chart',
                    chartHtml: parsed.metadata.chart_html,
                    chartId: parsed.metadata.chart_id,
                    chartType: parsed.metadata.chart_type
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'message_complete') {
                // Complete message - show immediately (tool results, etc.)
                if (parsed.content && parsed.content[0] && parsed.content[0].text) {
                  accumulatedText = parsed.content[0].text;
                  currentStatus = null;
                  yield {
                    content: [{ type: 'tool-result', text: accumulatedText }]
                  };
                }
              } else if (parsed.type === 'file_download') {
                // File download - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.file_data && parsed.metadata.filename) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'file_download',
                    filename: parsed.metadata.filename,
                    fileData: parsed.metadata.file_data,
                    format: parsed.metadata.format,
                    sizeBytes: parsed.metadata.size_bytes
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'message_ids') {
                setTimeout(() => {
                  const allMessages = document.querySelectorAll('[data-role]');
                  if (allMessages.length >= 2) {
                    const userMsg = allMessages[allMessages.length - 2];
                    const assistantMsg = allMessages[allMessages.length - 1];
                    if (userMsg && parsed.user_message_id) {
                      userMsg.setAttribute('data-message-id', parsed.user_message_id);
                    }
                    if (assistantMsg && parsed.assistant_message_id) {
                      assistantMsg.setAttribute('data-message-id', parsed.assistant_message_id);
                    }
                  }
                }, 100);
                loadThreads(selectedGptId);
              } else if (parsed.type === 'error') {
                // Error from backend - append error text directly
                const errorMessage = parsed.error?.message || 'An error occurred';
                accumulatedText += (accumulatedText ? '\n\n' : '') + `**Error:** ${errorMessage}`;
                yield { content: buildContentParts() };
              } else if (parsed.type === 'done') {
                break;
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }

        return { content: buildContentParts() };
      } catch (error) {
        if (error.name === 'AbortError') {
          return { content: [{ type: 'text', text: '' }] };
        }
        throw error;
      }
    }
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}

export default function ChatInterfaceFull({ onLogout }) {
  // Get API URL from config
  const API_URL = window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';
  const token = localStorage.getItem('cme_token');

  // State
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('cme_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [company, setCompany] = useState(null);
  const [customGpts, setCustomGpts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedGptId, setSelectedGptId] = useState(() => {
    // Initially try localStorage, will be overridden by user data from API
    const stored = localStorage.getItem('cme_user');
    if (stored) {
      try {
        return JSON.parse(stored).last_selected_gpt || '';
      } catch {
        return '';
      }
    }
    return '';
  });

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Share State
  const [sharedThreads, setSharedThreads] = useState([]); // Threads shared with current user
  const [currentThreadSharedWith, setCurrentThreadSharedWith] = useState([]); // Users the current thread is shared with
  const [isCurrentThreadOwner, setIsCurrentThreadOwner] = useState(true); // Is current user the owner of current thread
  const [companyUsers, setCompanyUsers] = useState([]); // All users in the company for sharing

  // Chat State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageFiles, setMessageFiles] = useState([]);
  const threadIdRef = useRef(null);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const isLoadingThreadRef = useRef(false);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [runtimeKey, setRuntimeKey] = useState(0); // Used to force runtime recreation

  // Check if impersonating
  const isImpersonating = localStorage.getItem('cme_impersonating') === 'true';

  // Dark mode state - read from user object in localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('cme_user');
    if (stored) {
      try {
        return JSON.parse(stored).theme === 'dark';
      } catch {
        return false;
      }
    }
    return false;
  });

  // Get conversation starters for selected GPT
  const getConversationStarters = () => {
    if (!selectedGptId) return [];
    const gpt = customGpts.find(g => g.id === selectedGptId);
    return gpt?.conversation_starters || [];
  };

  // Initialize from cached user data (App.jsx fetches fresh data on mount)
  // Returns the last_selected_gpt value for use in loading threads
  const loadUserInfo = useCallback(async () => {
    try {
      // Read from localStorage (App.jsx already fetched fresh data)
      const stored = localStorage.getItem('cme_user');
      if (!stored) return '';

      const userInfo = JSON.parse(stored);
      setUser(userInfo);

      // Apply theme and update state
      const userTheme = userInfo.theme || 'light';
      applyTheme(userTheme);
      setIsDarkMode(userTheme === 'dark');

      // Set last selected GPT from user data
      const gptId = userInfo.last_selected_gpt || '';
      if (gptId) {
        setSelectedGptId(gptId);
      }

      // Load company info
      if (userInfo.company_id) {
        const companyRes = await fetch(`${API_URL}/companies/${userInfo.company_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (companyRes.ok) {
          const companyData = await companyRes.json();
          setCompany(companyData);
          document.title = companyData.name;

          // Cache company data for next page load (used by inline script to avoid favicon flicker)
          localStorage.setItem('cme_company', JSON.stringify(companyData));

          // Update favicon and apple-touch-icon
          if (companyData.favicon_url) {
            const faviconUrl = `${API_URL}${companyData.favicon_url}`;
            const favicon = document.getElementById('dynamic-favicon');
            const appleIcon = document.getElementById('dynamic-apple-icon');
            if (favicon) favicon.href = faviconUrl;
            if (appleIcon) appleIcon.href = faviconUrl;
          }
        }
      }

      return gptId;
    } catch (error) {
      console.error('Failed to load user info:', error);
      return '';
    }
  }, [API_URL, token]);

  // Load custom GPTs
  const loadCustomGpts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/custom-gpts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const gpts = await response.json();
        setCustomGpts(gpts);
      }
    } catch (error) {
      console.error('Failed to load custom GPTs:', error);
    }
  }, [API_URL, token]);

  // Load thread history (filtered by selected GPT)
  // Note: gptId must be passed explicitly - we don't use selectedGptId from closure
  // to avoid recreating this callback when selectedGptId changes
  const loadThreads = useCallback(async (gptId = '') => {
    try {
      const params = { limit: 50 };
      // Filter by custom_gpt_id:
      // - If a GPT is selected: filter for that GPT's threads
      // - If no GPT selected (empty string): filter for default chat threads
      // Backend interprets empty string as "threads with no GPT assigned"
      params.custom_gpt_id = gptId;

      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.list',
          params
        })
      });

      if (response.ok) {
        const result = await response.json();
        setThreads(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
    }
  }, [API_URL, token]);

  // Load threads shared with current user
  const loadSharedThreads = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.shared',
          params: {}
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSharedThreads(result.threads || []);
      }
    } catch (error) {
      console.error('Failed to load shared threads:', error);
    }
  }, [API_URL, token]);

  // Load participants for a thread (who it's shared with)
  const loadThreadParticipants = useCallback(async (threadId) => {
    if (!threadId) {
      setCurrentThreadSharedWith([]);
      setIsCurrentThreadOwner(true);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.participants',
          params: { thread_id: threadId }
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentThreadSharedWith(result.participants || []);
        setIsCurrentThreadOwner(result.is_owner ?? true);
      }
    } catch (error) {
      console.error('Failed to load thread participants:', error);
      setCurrentThreadSharedWith([]);
      setIsCurrentThreadOwner(true);
    }
  }, [API_URL, token]);

  // Apply theme
  const applyTheme = (theme) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Dispatch event for MUI theme sync
    window.dispatchEvent(new Event('themeChanged'));
  };

  // Handle theme toggle from header icon
  const handleToggleTheme = async () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);

    // Update localStorage FIRST (before dispatching event)
    const updatedUser = { ...user, theme: newTheme };
    localStorage.setItem('cme_user', JSON.stringify(updatedUser));
    setUser(updatedUser);

    // Apply theme and dispatch event (App.jsx reads from localStorage)
    applyTheme(newTheme);

    // Focus the chat input after theme change to trigger repaint
    setTimeout(() => {
      const input = document.querySelector('[data-composer-input]') ||
                    document.querySelector('textarea[placeholder]');
      if (input) input.focus();
    }, 50);

    // Save to server in background
    try {
      await fetch(`${API_URL}/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ theme: newTheme })
      });
    } catch (err) {
      console.error('Failed to save theme preference:', err);
    }
  };

  // Load thread messages and convert to assistant-ui format
  const loadThreadMessages = useCallback(async (threadId) => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.get',
          params: { thread_id: threadId, limit: 100 }
        })
      });

      if (!response.ok) {
        console.error('Failed to load thread:', response.status);
        return { messages: [], gptId: null, files: [] };
      }

      const thread = await response.json();
      const items = thread.items?.data || [];

      // Extract files per user message (for messageFiles context)
      // messageFiles is an array where each index corresponds to a user message
      const userMessageFiles = [];

      // Convert to ThreadMessageLike format with metadata for charts
      const messages = items.map(msg => {
        // Preserve content structure - can be string or array of parts (text, image, etc.)
        let content = msg.content;

        // Track attachments for user messages (for messageFiles context used by real-time messages)
        const attachments = msg.attachments || [];
        if (msg.role === 'user') {
          userMessageFiles.push(attachments);
        }

        return {
          id: msg.id,
          role: msg.role,
          content: content, // Preserve original structure (string or array with images)
          metadata: msg.metadata, // Include metadata for chart_html
          attachments: attachments, // Include attachments for history rendering
          createdAt: msg.created_at ? new Date(msg.created_at) : undefined,
          user_id: msg.user_id, // For showing message author in shared threads
        };
      });

      return { messages, gptId: thread.custom_gpt_id, files: userMessageFiles };
    } catch (error) {
      console.error('Error loading thread messages:', error);
      return { messages: [], gptId: null, files: [] };
    }
  }, [API_URL, token]);

  // Initial load
  useEffect(() => {
    if (!token) {
      onLogout?.();
      return;
    }

    const initialize = async () => {
      // Load user info first to get last_selected_gpt
      const gptId = await loadUserInfo();
      loadCustomGpts();
      loadSharedThreads(); // Load threads shared with this user

      // Check for thread ID in URL
      const urlParams = new URLSearchParams(window.location.search);
      const threadIdFromUrl = urlParams.get('thread');

      if (threadIdFromUrl) {
        // Load existing thread
        isLoadingThreadRef.current = true;
        const { messages, gptId: threadGptId, files } = await loadThreadMessages(threadIdFromUrl);

        threadIdRef.current = threadIdFromUrl;
        setCurrentThreadId(threadIdFromUrl);
        setHistoryMessages(messages);
        setMessageFiles(files || []); // Restore attachments from loaded thread
        loadThreadParticipants(threadIdFromUrl); // Load who this thread is shared with

        // Use thread's GPT if available, otherwise use user's last selected
        const effectiveGptId = threadGptId || gptId;
        if (threadGptId) {
          setSelectedGptId(threadGptId);
          // Update cached user data
          const stored = localStorage.getItem('cme_user');
          if (stored) {
            const userData = JSON.parse(stored);
            userData.last_selected_gpt = threadGptId;
            localStorage.setItem('cme_user', JSON.stringify(userData));
          }
        }

        // Load threads for the effective GPT
        loadThreads(effectiveGptId);
        isLoadingThreadRef.current = false;
      } else {
        // Start with a new thread
        threadIdRef.current = crypto.randomUUID();
        setHistoryMessages([]);
        // Load threads for user's last selected GPT
        loadThreads(gptId);
      }

      setIsLoadingHistory(false);
    };

    initialize();
  }, [token, loadUserInfo, loadCustomGpts, loadThreads, loadThreadMessages, loadSharedThreads, loadThreadParticipants]);

  // Handle GPT selection
  const handleSelectGPT = async (gptId) => {
    const newGptId = gptId || null;

    if (selectedGptId !== newGptId) {
      setSelectedGptId(newGptId || '');

      // Persist selection to user profile
      try {
        await fetch(`${API_URL}/users/me`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ last_selected_gpt: newGptId || '' })
        });
        // Update local storage user data
        const stored = localStorage.getItem('cme_user');
        if (stored) {
          const userData = JSON.parse(stored);
          userData.last_selected_gpt = newGptId || null;
          localStorage.setItem('cme_user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('Failed to save GPT selection:', error);
      }

      // Load threads for this GPT
      loadThreads(newGptId || '');

      // Start new chat when switching GPTs
      if (!isLoadingThreadRef.current) {
        handleNewChat();
      }
    }
  };

  // Handle new chat
  const handleNewChat = () => {
    // Clear history and start fresh - DOM manipulation
    threadIdRef.current = crypto.randomUUID();
    setCurrentThreadId(null);
    setHistoryMessages([]);
    setMessageFiles([]);
    setCurrentThreadSharedWith([]); // Reset sharing state
    setIsCurrentThreadOwner(true); // User owns new threads
    setRuntimeKey(prev => prev + 1); // Force runtime recreation
  };

  // Handle thread selection (also reloads if clicking same thread)
  const handleSelectThread = async (threadId) => {
    // Load thread messages - DOM manipulation, no URL changes
    isLoadingThreadRef.current = true;

    const { messages, gptId, files } = await loadThreadMessages(threadId);

    threadIdRef.current = threadId;
    setCurrentThreadId(threadId);
    setHistoryMessages(messages);
    setMessageFiles(files || []); // Restore attachments from loaded thread
    setRuntimeKey(prev => prev + 1); // Force runtime recreation
    loadThreadParticipants(threadId); // Load who this thread is shared with

    if (gptId) {
      setSelectedGptId(gptId);
      // Update cached user data
      const stored = localStorage.getItem('cme_user');
      if (stored) {
        const userData = JSON.parse(stored);
        userData.last_selected_gpt = gptId;
        localStorage.setItem('cme_user', JSON.stringify(userData));
      }
    }

    isLoadingThreadRef.current = false;
  };

  // Handle thread rename
  const handleRenameThread = async (threadId, newTitle) => {
    try {
      await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.update',
          params: {
            thread_id: threadId,
            title: newTitle
          }
        })
      });
      await loadThreads(selectedGptId);
    } catch (error) {
      console.error('Failed to rename thread:', error);
    }
  };

  // Handle thread delete
  const handleDeleteThread = async (threadId) => {
    try {
      await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.delete',
          params: { thread_id: threadId }
        })
      });

      // If the deleted thread is the currently selected one, start a new chat
      if (threadId === currentThreadId) {
        handleNewChat();
      }

      // Remove the thread from local state (preserves scroll position)
      setThreads(prev => prev.filter(t => t.id !== threadId));
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  // Handle thread pin/unpin
  const handlePinThread = async (threadId) => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.pin',
          params: { thread_id: threadId }
        })
      });

      if (response.ok) {
        // Reload threads to get updated order (pinned first)
        await loadThreads(selectedGptId);
      }
    } catch (error) {
      console.error('Failed to pin/unpin thread:', error);
    }
  };

  // Load company users for sharing
  const loadCompanyUsers = async () => {
    if (!user?.company_id) return;
    try {
      const response = await fetch(`${API_URL}/companies/${user.company_id}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const users = await response.json();
        setCompanyUsers(users);
      }
    } catch (error) {
      console.error('Failed to load company users:', error);
    }
  };

  // Check if a user has access to a GPT
  const userHasGptAccess = (userId, gpt) => {
    if (!gpt) return true; // No GPT = default chat, everyone has access
    if (gpt.user_id === userId) return true; // Owner always has access
    if (gpt.is_shared) return true; // Shared with all company users
    if (gpt.shared_with_users?.includes(userId)) return true; // Specifically shared
    return false;
  };

  // Load company users when viewing a shared thread (so we can show author names)
  useEffect(() => {
    const isSharedThread = !isCurrentThreadOwner || currentThreadSharedWith.length > 0;
    if (isSharedThread && companyUsers.length === 0) {
      loadCompanyUsers();
    }
  }, [isCurrentThreadOwner, currentThreadSharedWith]);

  // Handle opening share modal
  const handleOpenShareModal = async () => {
    setShareModalOpen(true);
    // Load company users if not already loaded
    if (companyUsers.length === 0) {
      await loadCompanyUsers();
    }
    // Refresh participants list for current thread
    if (currentThreadId) {
      await loadThreadParticipants(currentThreadId);
    }
  };

  // Get users who don't have access to the current GPT (for disabling in share modal)
  const currentGpt = customGpts.find(g => g.id === selectedGptId);
  const usersWithoutGptAccess = selectedGptId && currentGpt
    ? companyUsers.filter(u => !userHasGptAccess(u.id, currentGpt)).map(u => u.id)
    : [];

  // Handle sharing a thread with a user
  const handleShareThread = async (userId) => {
    if (!currentThreadId) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.share',
          params: {
            thread_id: currentThreadId,
            user_id: userId
          }
        })
      });
      if (response.ok) {
        // Refresh the shared-with list for current thread
        const userToAdd = companyUsers.find(u => u.id === userId);
        if (userToAdd) {
          setCurrentThreadSharedWith(prev => [...prev, userToAdd]);
        }
      }
    } catch (error) {
      console.error('Failed to share thread:', error);
    }
  };

  // Handle removing access from a thread
  const handleRemoveThreadAccess = async (userId) => {
    if (!currentThreadId) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.unshare',
          params: {
            thread_id: currentThreadId,
            user_id: userId
          }
        })
      });
      if (response.ok) {
        setCurrentThreadSharedWith(prev => prev.filter(u => u.id !== userId));
      }
    } catch (error) {
      console.error('Failed to remove thread access:', error);
    }
  };

  // Handle leaving a shared thread (user removes themselves)
  const handleLeaveThread = async (threadId) => {
    if (!threadId || !user?.id) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.unshare',
          params: {
            thread_id: threadId,
            user_id: user.id
          }
        })
      });
      if (response.ok) {
        // Remove from shared threads list
        setSharedThreads(prev => prev.filter(t => t.id !== threadId));
        // If the left thread is currently selected, start a new chat
        if (threadId === currentThreadId) {
          handleNewChat();
        }
      }
    } catch (error) {
      console.error('Failed to leave thread:', error);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('cme_token');
    localStorage.removeItem('cme_user');
    localStorage.removeItem('cme_original_token');
    localStorage.removeItem('cme_impersonating');
    onLogout?.();
  };

  // Handle return to super admin
  const handleReturnToSuperAdmin = () => {
    const originalToken = localStorage.getItem('cme_original_token');
    if (originalToken) {
      localStorage.setItem('cme_token', originalToken);
      localStorage.removeItem('cme_original_token');
      localStorage.removeItem('cme_impersonating');
      window.location.href = 'super-admin.html';
    }
  };

  // Handle conversation starter click
  const handleStarterClick = (prompt) => {
    // Find the composer input and fill it
    const textarea = document.querySelector('[placeholder="Type your message..."]');
    if (textarea) {
      // Set value
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(textarea, prompt);

      // Trigger input event
      textarea.dispatchEvent(new Event('input', { bubbles: true }));

      // Submit the form
      setTimeout(() => {
        const form = textarea.closest('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 100);
    }
  };

  // Show loading state while loading history
  if (isLoadingHistory) {
    return (
      <Box
        className="app-container"
        sx={{
          bgcolor: 'background.default',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <FilesContext.Provider value={{ selectedFiles, setSelectedFiles }}>
      <MessageFilesContext.Provider value={{ messageFiles }}>
        <ChatRuntimeWrapper
          key={runtimeKey}
          threadIdRef={threadIdRef}
          selectedGptId={selectedGptId}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          setMessageFiles={setMessageFiles}
          loadThreads={loadThreads}
          API_URL={API_URL}
          token={token}
        >
          <Box className="app-container" sx={{ bgcolor: 'background.default' }}>
            {/* Sidebar */}
            <Sidebar
              isOpen={sidebarOpen}
              isCollapsed={sidebarCollapsed}
              onClose={() => setSidebarOpen(false)}
              onCollapse={() => setSidebarCollapsed(true)}
              onExpand={() => setSidebarCollapsed(false)}
              onNewChat={handleNewChat}
              onSearchChats={() => setSearchModalOpen(true)}
              onSelectGPT={handleSelectGPT}
              onSelectThread={handleSelectThread}
              selectedGptId={selectedGptId}
              threads={threads}
              sharedThreads={sharedThreads}
              currentThreadId={currentThreadId}
              customGpts={customGpts}
              user={user}
              onOpenProfile={() => setProfileModalOpen(true)}
              onLogout={handleLogout}
              onOpenCompanyAdmin={() => window.location.href = '/company-admin.html'}
              apiUrl={API_URL}
              onRenameThread={handleRenameThread}
              onDeleteThread={handleDeleteThread}
              onLeaveThread={handleLeaveThread}
              onPinThread={handlePinThread}
            />

            {/* Main Content */}
            <Box
              component="main"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Header
                onOpenMobileSidebar={() => setSidebarOpen(true)}
                company={company}
                isImpersonating={isImpersonating}
                onReturnToSuperAdmin={handleReturnToSuperAdmin}
                isDarkMode={isDarkMode}
                onToggleTheme={handleToggleTheme}
                userRole={user?.role}
                logoMinWidth="1750px"
                currentThreadId={currentThreadId}
                threadSharedWith={currentThreadSharedWith}
                isThreadOwner={isCurrentThreadOwner}
                onShareClick={handleOpenShareModal}
              />

              {/* Chat Area */}
              <div
                className="chat-container"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <SharedThreadContext.Provider value={{
                  isSharedThread: !isCurrentThreadOwner || currentThreadSharedWith.length > 0,
                  companyUsers,
                  currentUserId: user?.id,
                }}>
                  <Thread
                    historyMessages={historyMessages}
                    conversationStartersSlot={
                      <ConversationStarters
                        starters={getConversationStarters()}
                        onSelectStarter={handleStarterClick}
                      />
                    }
                  />
                </SharedThreadContext.Provider>
              </div>
            </Box>

            {/* Modals */}
            <ProfileModal
              isOpen={profileModalOpen}
              onClose={() => setProfileModalOpen(false)}
              user={user}
              apiUrl={API_URL}
              onUserUpdate={setUser}
              onThemeChange={applyTheme}
            />

            <SearchChatsModal
              isOpen={searchModalOpen}
              onClose={() => setSearchModalOpen(false)}
              onSelectThread={handleSelectThread}
              apiUrl={API_URL}
            />

            <ShareModal
              isOpen={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              threadId={currentThreadId}
              threadTitle={threads.find(t => t.id === currentThreadId)?.title || sharedThreads.find(t => t.id === currentThreadId)?.title}
              sharedWith={currentThreadSharedWith}
              isOwner={isCurrentThreadOwner}
              companyUsers={companyUsers}
              currentUserId={user?.id}
              onShare={handleShareThread}
              onRemoveAccess={handleRemoveThreadAccess}
              usersWithoutGptAccess={usersWithoutGptAccess}
              gptName={currentGpt?.name}
            />
          </Box>

          <style>{`
            /* No animation on messages to prevent scroll jump issues */
            .chat-message-enter {
              /* Animation disabled - was causing scroll jump when new messages are added */
            }

            .chat-message-bubble {
              transition: all 0.2s ease-out;
            }

            /* Hide message tools by default */
            .message-tools-container {
              opacity: 0 !important;
              visibility: visible !important;
              transition: opacity 0.2s ease-out;
            }

            /* Show on hover (desktop) */
            [data-role]:hover .message-tools-container {
              opacity: 1 !important;
            }

            /* Sidebar styling */
            #sidebar {
              height: 100%;
              overflow: hidden;
            }

            #historyScroll {
              flex: 1;
              min-height: 0;
            }
            /* History scrollbar styles are in nnaico.css */
          `}</style>
        </ChatRuntimeWrapper>
      </MessageFilesContext.Provider>
    </FilesContext.Provider>
  );
}
